/**
 * RecoveryTimeline — visual point-in-time scrubber for the restore browser
 * (Spec 201 — US4 / FR-019/022). Modeled on the Synology/Acronis restore timeline:
 * each recovery point is a marker positioned by time; click to select. Supports
 * zoom + pan so clustered points (e.g. several within a day) can be separated.
 *
 * Pure presentational — colours via inline style (CSS tokens + status hex) so it
 * stays design-blueprint conformant (no raw-gray utility classes).
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import React, { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Maximize2, Lock } from 'lucide-react';
import { AppTooltip } from '@/components/ui';
import { formatEastern, formatEasternDate } from '@/utils/dateFormatter';
import type { AssessedPoint } from '../../api/restore';

const MIN_WINDOW_MS = 5 * 60 * 1000; // don't zoom below a 5-minute window
const INSET_LO = 6;
const INSET_HI = 94;

const dotColor = (p: AssessedPoint): string =>
  !p.restorable ? '#ef4444' : p.backupType === 'full' ? 'var(--color-accent)' : '#94a3b8';

const isLockedOrHeld = (p: AssessedPoint): boolean =>
  p.immutabilityState === 'locked' || p.immutabilityState === 'held' || Boolean(p.storageLegalHold);

// Spec 202 — immutability suffix for the point tooltip.
const immLabel = (p: AssessedPoint): string => {
  if (p.immutabilityState === 'held' || p.storageLegalHold) return ' · legal hold (undeletable)';
  if (p.immutabilityState === 'locked')
    return ` · immutable until ${p.immutableUntil ? formatEasternDate(p.immutableUntil) : '—'}`;
  if (p.immutabilityState === 'expired') return ' · lock expired';
  return '';
};

const clampPct = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

const CtrlBtn: React.FC<{ title: string; disabled?: boolean; onClick: () => void; children: React.ReactNode }> = ({ title, disabled, onClick, children }) => (
  <AppTooltip title={title}>
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded border border-[var(--color-border)] p-1 text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-card)] hover:text-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  </AppTooltip>
);

const Legend: React.FC = () => (
  <div className="flex items-center gap-4 text-[11px] text-[var(--color-text-secondary)]">
    <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: 'var(--color-accent)' }} /> full</span>
    <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: '#94a3b8' }} /> incremental</span>
    <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: '#ef4444' }} /> not restorable</span>
    <span className="flex items-center gap-1"><Lock className="h-2.5 w-2.5" style={{ color: 'var(--color-accent)' }} /> immutable / hold</span>
  </div>
);

export const RecoveryTimeline: React.FC<{
  points: AssessedPoint[];
  selectedRunId: string | null;
  onSelect: (p: AssessedPoint) => void;
}> = ({ points, selectedRunId, onSelect }) => {
  const sorted = useMemo(
    () => [...points].sort((a, b) => new Date(a.pointTime).getTime() - new Date(b.pointTime).getTime()),
    [points]
  );
  const fullMin = useMemo(() => Math.min(...sorted.map((p) => new Date(p.pointTime).getTime())), [sorted]);
  const fullMax = useMemo(() => Math.max(...sorted.map((p) => new Date(p.pointTime).getTime())), [sorted]);
  const fullSpan = Math.max(1, fullMax - fullMin);

  // Visible window [start, end] — defaults to the full range; reset when the set changes.
  const [view, setView] = useState({ start: fullMin, end: fullMax });
  useEffect(() => setView({ start: fullMin, end: fullMax }), [fullMin, fullMax]);

  const clampView = (s: number, e: number) => {
    const w = e - s;
    if (w >= fullSpan) return { start: fullMin, end: fullMax }; // fit
    if (s < fullMin) return { start: fullMin, end: fullMin + w };
    if (e > fullMax) return { start: fullMax - w, end: fullMax };
    return { start: s, end: e };
  };
  const winW = view.end - view.start;
  const center = (view.start + view.end) / 2;
  const atFull = winW >= fullSpan;
  const atMin = winW <= MIN_WINDOW_MS;
  const zoomIn = () => { const w = Math.max(MIN_WINDOW_MS, winW * 0.5); setView(clampView(center - w / 2, center + w / 2)); };
  const zoomOut = () => { const w = Math.min(fullSpan, winW * 2); setView(clampView(center - w / 2, center + w / 2)); };
  const panLeft = () => { const d = winW * 0.5; setView(clampView(view.start - d, view.end - d)); };
  const panRight = () => { const d = winW * 0.5; setView(clampView(view.start + d, view.end + d)); };
  const fit = () => setView({ start: fullMin, end: fullMax });

  const leftPct = (t: number) => (winW === 0 ? 50 : INSET_LO + ((t - view.start) / winW) * (INSET_HI - INSET_LO));
  const visible = useMemo(
    () => sorted.filter((p) => { const t = new Date(p.pointTime).getTime(); return t >= view.start && t <= view.end; }),
    [sorted, view]
  );
  const hidden = sorted.length - visible.length;
  const selected = visible.find((p) => p.runId === selectedRunId) ?? null;
  const selPct = selected ? clampPct(leftPct(new Date(selected.pointTime).getTime()), 12, 88) : null;

  const fmtAxis = (t: number) => (winW < DAY ? formatEastern(new Date(t)) : formatEasternDate(new Date(t)));

  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-4 pb-3 pt-3">
      {/* toolbar */}
      <div className="mb-8 flex items-center justify-between gap-3">
        <Legend />
        {sorted.length > 1 && (
          <div className="flex items-center gap-1">
            <CtrlBtn title="Pan earlier" disabled={view.start <= fullMin} onClick={panLeft}><ChevronLeft className="h-3.5 w-3.5" /></CtrlBtn>
            <CtrlBtn title="Zoom out" disabled={atFull} onClick={zoomOut}><ZoomOut className="h-3.5 w-3.5" /></CtrlBtn>
            <CtrlBtn title="Zoom in" disabled={atMin} onClick={zoomIn}><ZoomIn className="h-3.5 w-3.5" /></CtrlBtn>
            <CtrlBtn title="Pan later" disabled={view.end >= fullMax} onClick={panRight}><ChevronRight className="h-3.5 w-3.5" /></CtrlBtn>
            <CtrlBtn title="Fit all" disabled={atFull} onClick={fit}><Maximize2 className="h-3.5 w-3.5" /></CtrlBtn>
          </div>
        )}
      </div>

      {/* track */}
      <div className="relative h-1 rounded-full bg-[var(--color-border)]">
        {/* selected timestamp badge (clamped so it never clips the card) */}
        {selected && selPct != null && (
          <span
            style={{ left: `${selPct}%`, backgroundColor: 'var(--color-accent)' }}
            className="absolute bottom-full mb-2 -translate-x-1/2 whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium text-white shadow-[var(--shadow-md)]"
          >
            {formatEastern(selected.pointTime)}
          </span>
        )}
        {visible.map((p) => {
          const t = new Date(p.pointTime).getTime();
          const isSel = p.runId === selectedRunId;
          return (
            <AppTooltip
              key={p.runId}
              title={`${p.backupType} · ${formatEastern(p.pointTime)} · ${p.tableCount ?? '—'} tables · ${p.restorable ? 'restorable' : p.notRestorableReason ?? 'not restorable'}${immLabel(p)}`}
            >
              <button
                type="button"
                onClick={() => onSelect(p)}
                style={{ left: `${leftPct(t)}%` }}
                className="absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                aria-label={`Recovery point ${formatEastern(p.pointTime)} (${p.backupType})`}
              >
                {isSel && <span className="absolute left-1/2 top-1/2 h-5 w-px -translate-x-1/2" style={{ backgroundColor: 'var(--color-accent)' }} />}
                {isLockedOrHeld(p) && (
                  <Lock
                    className="absolute -top-3 left-1/2 h-2.5 w-2.5 -translate-x-1/2"
                    style={{ color: 'var(--color-accent)' }}
                    aria-hidden
                  />
                )}
                <span
                  className={clsx('block rounded-full ring-2 transition-all', isSel ? 'h-4 w-4' : 'h-3 w-3 hover:h-3.5 hover:w-3.5')}
                  style={{ backgroundColor: dotColor(p), boxShadow: isSel ? '0 0 0 2px var(--color-accent)' : undefined, '--tw-ring-color': 'var(--color-bg-card)' } as React.CSSProperties}
                />
              </button>
            </AppTooltip>
          );
        })}
      </div>

      {/* axis labels */}
      <div className="mt-3 flex justify-between text-[11px] text-[var(--color-text-secondary)]">
        <span>{fmtAxis(view.start)}</span>
        <span>
          {visible.length} of {sorted.length} point{sorted.length === 1 ? '' : 's'}
          {hidden > 0 ? ` · ${hidden} outside view` : ''}
        </span>
        <span>{fmtAxis(view.end)}</span>
      </div>
    </div>
  );
};

const DAY = 24 * 60 * 60 * 1000;

export default RecoveryTimeline;
